import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import AutoResizeTextarea from '../components/customFields/autoResizeTextarea';
import CustomSelect from '../components/customFields/customSelect';

import apiCalls from '../utils/apiCalls';
import utils from '../utils/utils';

import GeneralForm from '../components/generalForm';
import { connect } from 'react-redux';
import MarketplaceDeliveryPreferences from '../components/customFields/marketplaceDeliveryPreferences';
import { withRouter } from 'react-router-dom';
import { setMarketplace } from './marketplacesActions';
import MarketplaceBillDetails from '../components/customFields/marketplaceBillDetails';
import icon from '../assets/icons/form-marketplace.svg';
import MARKETPLACE_PAYOUT_INTERVAL from '../components/constants/marketplacePayoutInterval';
import MARKETPLACE_TRANSPORT_TYPE from '../components/constants/marketplaceTransportType';
import MarketplaceFees from '../components/customFields/marketplaceFees';

const MarketplaceAdminForm = ({ marketplace, history, intl, setMarketplace, action, currentUser }) => {
  const [image, setImage] = useState(marketplace ? marketplace.image : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newMarketplace, setNewMarketplace] = useState(
    marketplace || {
      type: 'SHOP',
      deliveryPreferences: [
        {
          deliveryDay: 'TUESDAY',
          deadlineDay: 'SUNDAY',
          totalSumLimit: '',
          deadlineHour: 20,
        },
        {
          deliveryDay: 'THURSDAY',
          deadlineDay: 'TUESDAY',
          totalSumLimit: '',
          deadlineHour: 20,
        },
        {
          deliveryDay: 'FRIDAY',
          deadlineDay: 'WEDNESDAY',
          totalSumLimit: '',
          deadlineHour: 20,
        },
      ],
      fees: [
        {
          orderTotal: '0',
          feePercentage: '35',
        },
      ],
    }
  );

  useEffect(() => {
    if (marketplace) {
      setNewMarketplace(getMarketplaceFormObject(marketplace));
      setImage(marketplace.image);
    }
  }, [marketplace]);

  const payoutIntervalOptions = Object.keys(MARKETPLACE_PAYOUT_INTERVAL).map((key) => {
    const type = MARKETPLACE_PAYOUT_INTERVAL[key];
    return {
      value: type,
      label: intl.formatMessage({ id: 'marketplace.form.field.payout.interval.name.' + type }),
    };
  });

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      name: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.name.label' }),
      },
      description: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.description.label' }),
        default: '',
      },
      categories: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.categories.label' }),
      },
      orderEmails: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.order.emails.label' }),
      },
      emptyOrderEmailsDisabled: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'marketplace.form.field.end.empty.order.emails.disabled.label' }),
      },
      billDetails: {
        type: 'object',
        properties: {
          className: {
            type: 'String',
            default: 'CompanyBillDetails',
          },
          iban: {
            type: 'string',
            default: '',
          },
          companyName: {
            type: 'string',
            default: '',
          },
          vatNumber: {
            type: 'string',
            default: '',
          },
          registryCode: {
            type: 'string',
            default: '',
          },
          fullName: {
            type: 'string',
            default: '',
          },
          address: {
            type: 'object',
            default: {
              display: '',
            },
          },
          contactDetails: {
            type: 'object',
            default: {
              email: '',
              phoneNumber: '',
            },
          },
        },
      },
      endConsumerBillsEnabled: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'marketplace.form.field.end.consumer.bills.enabled.label' }),
      },
      payoutInterval: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.payout.interval.label' }),
        options: payoutIntervalOptions,
      },
      payoutReportEmails: {
        type: 'string',
        title: intl.formatMessage({ id: 'marketplace.form.field.payout.report.emails.label' }),
      },
    },
  };

  if (utils.userIsAdmin(currentUser)) {
    formSchema.properties.deliveryPreferences = {
      type: 'array',
    };

    formSchema.properties.fees = {
      type: 'array',
    };

    const transportTypes = Object.keys(MARKETPLACE_TRANSPORT_TYPE).map((key) => {
      const type = MARKETPLACE_TRANSPORT_TYPE[key];
      return {
        value: type,
        label: intl.formatMessage({ id: 'marketplace.form.field.transport.type.name.' + type }),
      };
    });

    formSchema.properties.transportType = {
      type: 'string',
      title: intl.formatMessage({ id: 'marketplace.form.field.transport.type.label' }),
      options: transportTypes,
    };

    formSchema.properties.isBusinessToConsumer = {
      type: 'boolean',
      default: true,
      title: intl.formatMessage({ id: 'marketplace.form.field.is.business.to.consumer.label' }),
    };

    formSchema.properties.isBusinessToBusiness = {
      type: 'boolean',
      default: false,
      title: intl.formatMessage({ id: 'marketplace.form.field.is.business.to.business.label' }),
    };

    formSchema.properties.isPrivate = {
      type: 'boolean',
      default: false,
      title: intl.formatMessage({ id: 'marketplace.form.field.is.private.label' }),
    };

    formSchema.properties.hideAllItems = {
      type: 'boolean',
      default: false,
      title: intl.formatMessage({ id: 'marketplace.form.field.hide.all.items.label' }),
    };

    formSchema.properties.enableAllItemsForB2b = {
      type: 'boolean',
      default: false,
      title: intl.formatMessage({ id: 'marketplace.form.field.enable.all.items.for.b2b' }),
    };
  }

  const uiSchema = {
    description: {
      'ui:field': 'autoResizeTextarea',
      'ui:help': intl.formatMessage({ id: 'marketplace.form.field.description.help' }),
    },
    categories: {
      'ui:help': intl.formatMessage({ id: 'marketplace.form.field.categories.help' }),
    },
    orderEmails: {
      'ui:help': intl.formatMessage({ id: 'marketplace.form.field.order.emails.help' }),
    },
    payoutReportEmails: {
      'ui:help': intl.formatMessage({ id: 'marketplace.form.field.payout.report.emails.help' }),
    },
    endConsumerBillsEnabled: {
      'ui:help': intl.formatMessage({ id: 'marketplace.form.field.end.consumer.bills.enabled.help' }),
    },
    transportType: {
      'ui:field': 'customSelect',
    },
    payoutInterval: {
      'ui:field': 'customSelect',
    },
    deliveryPreferences: {
      'ui:field': 'marketplaceDeliveryPreferences',
    },
    fees: {
      'ui:field': 'marketplaceFees',
    },
    billDetails: {
      'ui:field': 'marketplaceBillDetails',
    },
  };

  function onFormSubmit() {
    setLoading(true);

    if (action === 'edit') {
      return apiCalls
        .editMarketplace({ marketplace: newMarketplace, image: image })
        .then((result) => {
          setMarketplace(result.data.marketplace);
          history.push({
            pathname: `/marketplaceAdmin/${result.data.marketplace.id}/view`,
          });
        })
        .catch((error) => {
          window.scrollTo(0, 0);
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
        });
    } else {
      return apiCalls
        .createMarketplace({ marketplace: newMarketplace, image: image })
        .then((result) => {
          setMarketplace(result.data.marketplace);
          history.push({
            pathname: `/marketplaceAdmin/${result.data.marketplace.id}/view`,
          });
        })
        .catch((error) => {
          window.scrollTo(0, 0);
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
        });
    }
  }

  const formFields = {
    autoResizeTextarea: AutoResizeTextarea,
    customSelect: CustomSelect,
    marketplaceFees: MarketplaceFees,
    marketplaceDeliveryPreferences: MarketplaceDeliveryPreferences,
    marketplaceBillDetails: MarketplaceBillDetails,
  };

  const images = image ? [image] : [];

  const header =
    action === 'edit' && !loading
      ? intl.formatMessage({ id: 'marketplace.edit.header' }, { marketplaceName: newMarketplace.name })
      : intl.formatMessage({ id: 'marketplace.create.header' });

  return (
    <div className="row">
      <div className="col-12 col-lg-10 offset-lg-1 marketplace-admin__form">
        <GeneralForm
          title={header}
          icon={icon}
          onChange={(event) => setNewMarketplace(event.formData)}
          onSubmit={onFormSubmit}
          uploadImage={(image) => apiCalls.uploadImage(image, 'marketplace', newMarketplace.id)}
          deleteImage={(image) => apiCalls.deleteImage(image)}
          onImagesChange={(images) => setImage(images[0])}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={newMarketplace}
          uploadedImages={images}
          buttonCopy={'marketplace.edit.form.submit'}
          errorMessage={error}
          loading={loading}
          singleImage={true}
        />
      </div>
    </div>
  );
};

function getMarketplaceFormObject(marketplace) {
  return {
    ...marketplace,
    categories: marketplace.categories ? marketplace.categories.join(', ') : '',
    orderEmails: marketplace.orderEmails ? marketplace.orderEmails.join(', ') : '',
    payoutReportEmails: marketplace.payoutReportEmails ? marketplace.payoutReportEmails.join(', ') : '',
    deliveryPreferences: marketplace.deliveryPreferences.map((dp) => {
      return {
        deliveryDay: dp.deliveryDay,
        deadlineDay: dp.deadlineDay,
        totalSumLimit: dp.totalSumLimit ? dp.totalSumLimit.toString() : '',
        deadlineHour: dp.deadlineHour ? dp.deadlineHour.toString() : '',
      };
    }),
    fees: marketplace.fees.map((dp) => {
      return {
        ...dp,
        orderTotal: `${dp.orderTotal}`,
        feePercentage: `${dp.feePercentage}`,
      };
    }),
  };
}

MarketplaceAdminForm.propTypes = {
  currentUser: PropTypes.object,
  marketplace: PropTypes.object,
  action: PropTypes.string,
};

MarketplaceAdminForm.defaultProps = {
  action: 'create',
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
    marketplace: state.marketplace.marketplace,
  };
}

const mapDispatchToProps = {
  setMarketplace,
};

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(MarketplaceAdminForm)));
